import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { AxiosResponse } from 'axios';
import { useAppDispatch } from '../../../../hooks';
import catalogApiClient from '../../../../services/catalogapi';
import { alertError } from '../../../../slices/global/alertSlice';

export interface ClassProps {
  id: number;
  value: string;
}

interface Props {
  value: ClassProps | null;
  setValue: React.Dispatch<React.SetStateAction<ClassProps | null>>;
  handleChange: (event: React.SyntheticEvent<Element, Event>, newValue: ClassProps | null) => void;
}

export default function ClassAutoComplete({ value, setValue, handleChange }: Props) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly ClassProps[]>([]);
  const [inputValue, setInputValue] = React.useState<string>('');
  const loading = open && options.length === 0;
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const controller = new AbortController();
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await catalogApiClient
        .get('api/v1/fitment/parttype/GetClasses', {
          signal: controller.signal,
        })
        .then((response: AxiosResponse<ClassProps[]>) => {
          if (active) {
            setOptions([...response.data]);
          }
        })
        .catch(function (err) {
          if (err.message !== 'canceled')
            dispatch(alertError(`Failed to retrieve edi files. Reason: ${err}`));
        });
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [loading, dispatch]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <Autocomplete
      disablePortal
      value={value}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      id="class-autocomplete"
      options={options}
      renderOption={(props, option) => <li {...props}>{option.value}</li>}
      getOptionLabel={(option) => option.value ?? ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      sx={{ width: 500 }}
      renderInput={(params) => <TextField {...params} label="Class" />}
    />
  );
}
